import React, { useEffect, useState } from "react";
import { Plus, Pencil, Printer, UserX, UserCheck, KeyRound, Eye, EyeOff, Info } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import api, { apiError } from "@/lib/api";
import { fmtDate } from "@/lib/constants";
import { useLang } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { Panel, Empty, RowSearch } from "@/pages/shared";
import UserForm, { blankUser } from "@/components/UserForm";
import UserSheet from "@/components/UserSheet";
import UserInfoDialog from "@/components/UserInfoDialog";

export default function UsersPanel() {
  const { t } = useLang();
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(blankUser());
  const [busy, setBusy] = useState(false);
  const [sheet, setSheet] = useState(null);
  const [info, setInfo] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [q, setQ] = useState("");
  const [tenants, setTenants] = useState([]);
  const [tenant, setTenant] = useState(me?.tenant_id || "");
  const isSuper = me?.role === "superadmin";
  const hdr = tenant && tenant !== me?.tenant_id ? { headers: { "X-Camping": tenant } } : {};

  const inactiveCount = users.filter((u) => !u.active).length;
  const term = q.trim().toLowerCase();
  const visible = (showInactive ? users : users.filter((u) => u.active))
    .filter((u) => !term || [u.nickname, u.first_name, u.last_name, u.email, u.phone,
      u.number, u.staff_number, u.camp_name, t(`role_${u.role}`)]
      .some((v) => String(v || "").toLowerCase().includes(term)));

  const load = async () => {
    try { setUsers((await api.get("/users", hdr)).data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, [tenant]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (isSuper) api.get("/tenants").then(({ data }) => setTenants(data)).catch(() => {});
  }, [isSuper]);

  const openNew = async () => {
    setForm(blankUser()); setEditId(null); setOpen(true);
    try {
      const { data } = await api.get("/users/next-number", hdr);
      setForm((f) => ({ ...f, number: data.number }));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const openEdit = (u) => { setForm({ ...blankUser(), ...u, workdays: { ...blankUser().workdays, ...(u.workdays || {}) } }); setEditId(u.id); setOpen(true); };

  const save = async () => {
    setBusy(true);
    try {
      const body = { ...form, idle_minutes: Math.max(60, Number(form.idle_minutes) || 60), min_week_hours: Number(form.min_week_hours) || 0 };
      if (editId) await api.put(`/users/${editId}`, body, hdr);
      else await api.post("/users", body, hdr);
      setOpen(false);
      await load();
      toast.success(editId ? t("saved") : t("user_created_hint"));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const resetPassword = async (u) => {
    if (!window.confirm(`${t("reset_pw_confirm")}\n\n${u.first_name} ${u.last_name} — ${u.email}${u.camp_name ? `\n${t("camping")}: ${u.camp_name}` : ""}`)) return;
    try {
      await api.post(`/users/${u.id}/reset-password`, {}, hdr);
      await load();
      toast.success(`${t("reset_pw_done")}: ${u.email}`, { description: t("reset_pw_hint"), duration: 10000 });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const toggleActive = async (u) => {
    try { await api.patch(`/users/${u.id}/active`, { active: !u.active }, hdr); await load(); toast.success(t("saved")); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const today = new Date().toISOString().slice(0, 10);
  const birthOk = !form.birth_date || form.birth_date <= today;
  const valid = form.number && form.first_name && form.last_name && form.email && birthOk;

  const RANK = { superadmin: 3, admin: 2, reception: 1, personeel: 1, controller: 1 };
  const canManage = (u) => me?.role === "superadmin" || (RANK[u.role] ?? 1) <= (RANK[me?.role] ?? 0);

  return (
    <Panel
      title={isSuper && tenants.length > 0 ? (
        <span className="flex items-center gap-2 flex-wrap">
          {t("users")} ·
          <Select value={tenant} onValueChange={setTenant}>
            <SelectTrigger className="h-8 w-auto min-w-[200px] font-display font-bold text-base" data-testid="users-tenant-select"><SelectValue /></SelectTrigger>
            <SelectContent>{tenants.map((c) => <SelectItem key={c.id} value={c.id} data-testid={`users-tenant-${c.id}`}>{c.name} ({c.users})</SelectItem>)}</SelectContent>
          </Select>
          {q && <span className="text-muted-foreground font-normal text-sm">({visible.length}/{users.length})</span>}
        </span>
      ) : `${t("users")} · ${me?.tenant_name || ""} (${visible.length}${q ? `/${users.length}` : ""})`}
      testid="users-panel"
      action={
        <div className="flex flex-wrap items-center gap-2 print:hidden">
          <RowSearch value={q} onChange={setQ} placeholder={t("users_search")} testid="users-search" />
          <Button variant="outline" onClick={() => setShowInactive((v) => !v)} data-testid="toggle-inactive-users">
            {showInactive ? <EyeOff className="h-4 w-4 mr-1" /> : <Eye className="h-4 w-4 mr-1" />}
            {showInactive ? t("users_hide_inactive") : `${t("users_show_inactive")} (${inactiveCount})`}
          </Button>
          <Button variant="outline" onClick={() => window.print()} data-testid="print-users"><Printer className="h-4 w-4 mr-1" />{t("print")}</Button>
          <Button onClick={openNew} data-testid="add-user-button"><Plus className="h-4 w-4 mr-1" />{t("new_user")}</Button>
        </div>
      }
    >
      {visible.length === 0 ? <Empty /> : (
        <div className="max-h-[70vh] overflow-auto cb-scroll overscroll-contain" data-testid="users-scroll">
        <Table className="print-users">
          <TableHeader className="sticky top-0 z-10 bg-card">
            <TableRow>
              <TableHead>{t("first_name")}</TableHead>
              <TableHead>{t("last_name")}</TableHead>
              <TableHead>{t("phone")}</TableHead>
              <TableHead>{t("u_role")}</TableHead>
              <TableHead>{t("language")}</TableHead>
              <TableHead>{t("status")}</TableHead>
              <TableHead className="print:hidden"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visible.map((u) => (
              <TableRow key={u.id} data-testid={`user-row-${u.number}`} className={u.active ? "" : "opacity-50"}>
                <TableCell className="font-medium">{u.first_name}</TableCell>
                <TableCell>{u.last_name}</TableCell>
                <TableCell className="text-muted-foreground">{u.phone}</TableCell>
                <TableCell><span className="inline-flex px-2 py-0.5 rounded-full text-xs font-semibold bg-secondary text-primary">{t(`role_${u.role}`)}</span></TableCell>
                <TableCell className="uppercase text-xs font-semibold">{u.language}</TableCell>
                <TableCell>
                  {u.active
                    ? <span className="text-xs font-semibold text-emerald-600">{t("active")}{u.must_change_password ? ` · ${t("pw_pending")}` : ""}</span>
                    : <span className="text-xs font-semibold text-slate-400">{t("inactive")}</span>}
                </TableCell>
                <TableCell className="text-right whitespace-nowrap print:hidden">
                  <Button variant="ghost" size="sm" onClick={() => setInfo(u)} title={t("users_info")} data-testid={`info-user-${u.number}`}><Info className="h-4 w-4 text-primary" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => setSheet(u)} title={t("print")} data-testid={`print-user-${u.number}`}><Printer className="h-4 w-4" /></Button>
                  {canManage(u) && <>
                  <Button variant="ghost" size="sm" onClick={() => openEdit(u)} data-testid={`edit-user-${u.number}`}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => resetPassword(u)} title={t("reset_pw_tooltip")}
                    aria-label={t("reset_pw")} data-testid={`reset-pw-${u.number}`}>
                    <KeyRound className="h-4 w-4 text-amber-600" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => toggleActive(u)} disabled={u.id === me?.id} title={u.active ? t("deactivate") : t("activate")} data-testid={`toggle-user-${u.number}`}>
                    {u.active ? <UserX className="h-4 w-4 text-rose-500" /> : <UserCheck className="h-4 w-4 text-emerald-600" />}
                  </Button>
                  </>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-4xl max-h-[92vh] overflow-y-auto overflow-x-hidden cb-scroll" data-testid="user-dialog">
          <DialogHeader><DialogTitle className="font-display">{editId ? `${t("edit")} — ${form.nickname || form.first_name}` : t("new_user")}</DialogTitle></DialogHeader>
          <UserForm form={form} setForm={setForm} />
          {!birthOk && <p className="text-xs text-rose-600" data-testid="birth-error">{t("birth_future")}</p>}
          {!editId && <p className="text-xs text-muted-foreground bg-secondary/60 rounded-md px-3 py-2">{t("user_pw_info")}</p>}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t("cancel")}</Button>
            <Button onClick={save} disabled={!valid || busy} data-testid="save-user-button">{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {sheet && <UserSheet user={sheet} onClose={() => setSheet(null)} />}
      {info && <UserInfoDialog user={info} onClose={() => setInfo(null)} />}
    </Panel>
  );
}
